import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, ShieldAlert, CheckCircle2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useStuckInvoices, RecheckStripeButton, StripeStatusBadge, fmtUsd, fmtAge, runSweepNow } from './stuckInvoices';

const fmtWhen = (iso) => {
  if (!iso) return 'never';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return mins < 1 ? 'just now' : `${fmtAge(mins)} ago`;
};

/** Owner Admin: open checkouts older than 1h whose invoice is still unpaid, with one-click rescue. */
export default function StuckInvoicesPanel() {
  const { data, loading, refresh } = useStuckInvoices({ checkStripe: true, limit: 50 });
  const [manualId, setManualId] = useState('');
  const [sweeping, setSweeping] = useState(false);
  const items = data?.items || [];
  const stuckCount = data?.stuck_count || 0;
  const lastSweep = data?.last_sweep;

  const handleSweep = async () => {
    setSweeping(true);
    try {
      const res = await runSweepNow();
      if (res.error) toast.error(res.error);
      else if (res.healed_count) toast.success(`Sweep recorded ${res.healed_count} stuck payment${res.healed_count > 1 ? 's' : ''}`);
      else toast.info(`Sweep checked ${res.checked} checkout${res.checked === 1 ? '' : 's'} — nothing stuck`);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSweeping(false);
    }
  };

  return (
    <Card className="bg-card border-white/15" data-testid="stuck-invoices-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <ShieldAlert className={`h-5 w-5 ${stuckCount ? 'text-red-400' : 'text-emerald-400'}`} />
            Unreconciled Checkouts
            {stuckCount > 0 && (
              <span className="text-xs rounded-full bg-red-500/20 text-red-300 px-2 py-0.5" data-testid="stuck-count-badge">
                {stuckCount} stuck
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Pay-link sessions opened over an hour ago whose invoice is still unpaid. Rows marked
            <span className="text-red-300"> Paid on Stripe</span> missed their webhook — click Re-check to record the payment.
          </CardDescription>
          <p className="text-xs text-muted-foreground mt-2" data-testid="last-sweep-info">
            Auto self-heal runs hourly · last sweep {fmtWhen(lastSweep?.ran_at)}
            {lastSweep ? ` · checked ${lastSweep.checked}, healed ${lastSweep.healed_count}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={handleSweep} disabled={sweeping} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="run-sweep-now">
            <Zap className={`h-4 w-4 mr-2 ${sweeping ? 'animate-pulse' : ''}`} /> {sweeping ? 'Sweeping…' : 'Run sweep now'}
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} className="border-white/20" data-testid="stuck-panel-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => e.preventDefault()}
          data-testid="manual-reconcile-form"
        >
          <Input
            value={manualId}
            onChange={(e) => setManualId(e.target.value.trim())}
            placeholder="Invoice ID or number (e.g. INV-1042) to re-check manually"
            className="max-w-md bg-white/5 border-white/15 text-white"
            data-testid="manual-reconcile-input"
          />
          {manualId && <RecheckStripeButton invoiceId={manualId} onDone={refresh} size="default" />}
        </form>

        {loading && !data ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 bg-white/10" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4" data-testid="stuck-empty-state">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" /> No stale checkouts — every paid session is reconciled.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-muted-foreground">Invoice</TableHead>
                <TableHead className="text-muted-foreground">Workspace</TableHead>
                <TableHead className="text-muted-foreground">Customer</TableHead>
                <TableHead className="text-muted-foreground text-right">Total</TableHead>
                <TableHead className="text-muted-foreground">Checkout age</TableHead>
                <TableHead className="text-muted-foreground">Stripe</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow
                  key={i.invoice_id}
                  className={`border-white/10 ${i.stuck ? 'bg-red-500/5' : ''}`}
                  data-testid={`stuck-row-${i.invoice_id}`}
                >
                  <TableCell className="text-white font-medium">#{i.invoice_no || i.invoice_id}</TableCell>
                  <TableCell className="text-slate-300">{i.workspace_name || i.workspace_id}</TableCell>
                  <TableCell className="text-slate-300">{i.customer_name || '—'}</TableCell>
                  <TableCell className="text-right text-white">{fmtUsd(i.total)}</TableCell>
                  <TableCell className="text-slate-300">{fmtAge(i.age_minutes)}</TableCell>
                  <TableCell><StripeStatusBadge status={i.stripe_payment_status} /></TableCell>
                  <TableCell className="text-right">
                    <RecheckStripeButton invoiceId={i.invoice_id} onDone={refresh} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
